import { QueryClient, useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { MutationConfig, queryClient, TypedMutationConfig } from '@/lib/react-query';
import { ApiResponse, AxiosErrorCustom, axiosInstance } from '@/lib/axios';
import { TConnectWhatsapp } from '../types/connect-whatsapp.type';
import {
  GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY,
  TGetListIntegrationWhatsappResponse,
} from './get-list-integration-whatsapp.api';
import { TChannel } from '../../../_shared/types/channel.type';
// import { TChatPlatformIntegration } from '../../_shared/types/chat-platform.type';

/**
 * Update Status Whatsapp Type and Schema for switch status
 */
export const switchStatusWhatsappValidationSchema = z.object({
  status: z.boolean({ message: 'value must be boolean!' }),
  organizationChannelId: z.string({ message: 'value must be string!' }),
});

export type TSwitchStatusWhatsappIntegration = z.infer<typeof switchStatusWhatsappValidationSchema>;
export type TSwitchStatusWhatsappIntegrationResponseFalse = ApiResponse<TChannel>;
export type TSwitchStatusWhatsappIntegrationResponseTrue = ApiResponse<TConnectWhatsapp>;

export const switchStatusWhatsappIntegration = async (
  data: TSwitchStatusWhatsappIntegration
): Promise<TSwitchStatusWhatsappIntegrationResponseFalse | TSwitchStatusWhatsappIntegrationResponseTrue> => {
  /**
   * Update Status Whatsapp API for switch status
   */
  const url = `integration/whatsapp/org-channel/${data.organizationChannelId}`;

  const config = {
    timeout: 0,
  };

  /**
   * We have different response type for different status
   * - If status is false, return TSwitchStatusWhatsappIntegrationResponseFalse
   * - If status is true, return TSwitchStatusWhatsappIntegrationResponseTrue
   */
  return data.status === false
    ? (axiosInstance.patch(
        url,
        { status: data.status },
        config
      ) as Promise<TSwitchStatusWhatsappIntegrationResponseFalse>)
    : (axiosInstance.patch(
        url,
        { status: data.status },
        config
      ) as Promise<TSwitchStatusWhatsappIntegrationResponseTrue>);
};

/**
 * Switch Status Whatsapp React-Query Hook
 */
type UseSwitchStatusWhatsappIntegrationOptions = {
  mutationConfig?: MutationConfig<typeof switchStatusWhatsappIntegration>;
};

export const useSwitchStatusWhatsappIntegration = ({ mutationConfig }: UseSwitchStatusWhatsappIntegrationOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation({
    mutationFn: switchStatusWhatsappIntegration,
    ...restConfig,
  });
};

/**
 * Optimistic Update WhatsApp Integration Status
 */
export const updateWhatsappIntegrationOptimistically = (
  queryClient: QueryClient,
  data: TSwitchStatusWhatsappIntegration
) => {
  queryClient.setQueryData<TGetListIntegrationWhatsappResponse>(
    [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY],
    (oldData: TGetListIntegrationWhatsappResponse | undefined) => {
      if (!oldData) return oldData;

      return {
        ...oldData,
        payload: {
          ...oldData.payload,
          rows: oldData.payload.rows.map((integration) =>
            integration.organizationChannelId === data.organizationChannelId
              ? { ...integration, status: data.status }
              : integration
          ),
        },
      };
    }
  );
};

/**
 * Switch Status Whatsapp React-Query Hook
 */
type UseSwitchStatusWhatsappIntegrationOptimisticallyOptions = {
  mutationConfig?: TypedMutationConfig<typeof switchStatusWhatsappIntegration, AxiosErrorCustom>;
};

export const useSwitchStatusWhatsappIntegrationOptimistically = ({
  mutationConfig,
}: UseSwitchStatusWhatsappIntegrationOptimisticallyOptions) => {
  const { ...restConfig } = mutationConfig || {};

  return useMutation<
    TSwitchStatusWhatsappIntegrationResponseFalse | TSwitchStatusWhatsappIntegrationResponseTrue,
    AxiosErrorCustom,
    TSwitchStatusWhatsappIntegration
  >({
    mutationFn: switchStatusWhatsappIntegration,
    onMutate: async (data: TSwitchStatusWhatsappIntegration) => {
      await queryClient.cancelQueries({ queryKey: [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY] });

      const previousData = queryClient.getQueryData([GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY]);

      // console.log({
      //   data,
      //   previousData,
      // });

      updateWhatsappIntegrationOptimistically(queryClient, data);

      return { previousData };
    },
    onError: (_err: unknown, _data: unknown, context: unknown) => {
      if (context && typeof context === 'object' && 'previousData' in context) {
        queryClient.setQueryData(
          [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY],
          (context as { previousData: unknown }).previousData
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_WHATSAPP_QUERY_KEY] });
    },
    ...restConfig,
  });
};
